import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Page, Pageable } from '../../../model/rest/base';
import {
  SchoolStudentStatus,
  V3Student,
} from '../../../model/v3/schools';
import { VerifiedPasswordChangeRequest } from '../../../model/v3/people';
import {appendCollection, appendIfDefined, createPageableParams, serializeDateInput} from './rest-v3-utils';

@Injectable({
  providedIn: 'root',
})
export class StudentV3RestService {
  constructor(private http: HttpClient) {}

  private url(schoolId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/students${path}`;
  }

  getStudents(
    schoolId: number,
    term?: string | null,
    status?: SchoolStudentStatus[] | null,
    expiry?: Date | string | null,
    pageable?: Pageable,
  ): Observable<Page<V3Student>> {
    let params = createPageableParams(pageable);
    const normalizedTerm = term?.trim();
    params = appendIfDefined(params, 'term', normalizedTerm ? normalizedTerm : undefined);
    params = appendCollection(params, 'status', status ?? undefined);
    params = appendIfDefined(params, 'expiry', serializeDateInput(expiry));
    return this.http.get<Page<V3Student>>(this.url(schoolId), { params });
  }

  getStudent(schoolId: number, studentId: number): Observable<V3Student> {
    return this.http.get<V3Student>(this.url(schoolId, `/${studentId}`));
  }

  updateStatus(
    schoolId: number,
    studentId: number,
    status: SchoolStudentStatus,
  ): Observable<void> {
    return this.http.patch<void>(this.url(schoolId, `/${studentId}/status`), JSON.stringify(status),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      });
  }

  updateExpiryDate(
    schoolId: number,
    studentId: number,
    expiryDate?: Date | string | null,
  ): Observable<void> {
    const serialized = serializeDateInput(expiryDate);
    return this.http.patch<void>(
      this.url(schoolId, `/${studentId}/exp-date`),
      JSON.stringify(serialized),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  deleteStudent(schoolId: number, studentId: number): Observable<void> {
    return this.http.delete<void>(this.url(schoolId, `/${studentId}`));
  }

  changeStudentPasswordInstantly(
    schoolId: number,
    studentId: number,
    request: VerifiedPasswordChangeRequest,
  ): Observable<void> {
    return this.http.patch<void>(
      this.url(schoolId, `/${studentId}/person/account/password`),
      request,
    );
  }
}
